import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { kml } from '@tmcw/togeojson';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

interface MapViewerProps {
  url: string;
  fileName: string;
}

export const MapViewer = ({ url, fileName }: MapViewerProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    const loadMap = async () => {
      try {
        setLoading(true);
        setError(null);

        // Crear mapa
        if (!mapRef.current) {
          mapRef.current = L.map(mapContainer.current).setView([4.5709, -74.2973], 6);

          // Agregar capa base
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(mapRef.current);
        }

        // Descargar archivo KML/KMZ
        const response = await fetch(url);
        if (!response.ok) throw new Error('Error al cargar el archivo');

        let kmlText: string;
        
        // Si es KMZ (ZIP), extraer el KML
        if (fileName.toLowerCase().endsWith('.kmz')) {
          const JSZip = (await import('jszip')).default;
          const arrayBuffer = await response.arrayBuffer();
          const zip = await JSZip.loadAsync(arrayBuffer);
          
          // Buscar archivo .kml dentro del ZIP
          const kmlFile = Object.keys(zip.files).find(name => name.toLowerCase().endsWith('.kml'));
          if (!kmlFile) throw new Error('No se encontró archivo KML dentro del KMZ');
          
          kmlText = await zip.files[kmlFile].async('string');
        } else {
          kmlText = await response.text();
        }

        // Parsear KML
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(kmlText, 'text/xml');
        
        // Verificar errores de parseo
        const parseError = kmlDoc.querySelector('parsererror');
        if (parseError) throw new Error('Error al parsear el archivo KML');

        // Convertir a GeoJSON
        const geoJson = kml(kmlDoc);

        // Agregar al mapa
        const layer = L.geoJSON(geoJson, {
          onEachFeature: (feature, layer) => {
            if (feature.properties) {
              const props = feature.properties;
              let popupContent = '<div class="p-2">';
              
              if (props.name) popupContent += `<strong>${props.name}</strong><br/>`;
              if (props.description) popupContent += `${props.description}<br/>`;
              
              // Agregar otras propiedades
              Object.keys(props).forEach(key => {
                if (key !== 'name' && key !== 'description' && props[key]) {
                  popupContent += `<span class="text-sm">${key}: ${props[key]}</span><br/>`;
                }
              });
              
              popupContent += '</div>';
              layer.bindPopup(popupContent);
            }
          }
        }).addTo(mapRef.current!);

        // Ajustar vista al contenido
        if (layer.getBounds().isValid()) {
          mapRef.current!.fitBounds(layer.getBounds(), { padding: [50, 50] });
        }

        setLoading(false);
      } catch (err) {
        console.error('Error cargando mapa:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar el archivo');
        setLoading(false);
      }
    };

    loadMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [url, fileName]);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="relative w-full h-[500px]">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )}
      <div ref={mapContainer} className="w-full h-full rounded-md border" />
    </div>
  );
};
